import React, { useState, useEffect } from 'react';
import { useLocation, matchPath, Link } from 'react-router-dom';
import { Row, Col, Button } from 'reactstrap';
import '../assets/css/fadeIn.css';
import '../assets/css/SchoolDetails.css'
import '../App.js';
import SchoolImage from '../assets/images/schoolImage.png';
import { Skeleton, Space } from 'antd';

const SchoolDetails = () => {
  const location = useLocation();

  const match = matchPath(
    {
      path: "/:schoolKey",
      exact: true,
      strict: false,
    },
    location.pathname
  );

  const schoolKey = match ? match.params.schoolKey : null;

  const [data, setData] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSchool();
  }, [schoolKey]);

  const fetchSchool = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://liltriangle.b4a.app/examVerification/getLandingPage",
        {
          method: "POST",
          body: JSON.stringify({ key: schoolKey }),
          headers: { "Content-type": "application/json; charset=UTF-8" },
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log(result);
        setData(result.result);
      } else {
        throw new Error('Network response was not ok.');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiryFormClick = () => {
    if (data.id) {
      window.location.href = `https://forms.liltriangle.com/enquiry-form/${data.id}`;
    }
  };

  return (
    <div className="school-details">
      {loading ? (
        <div className="loading-container">
          <Row className="skeleton-row">
            <Col lg="6">
              <div className="d-flex align-items-center mb-3">
                <Skeleton.Image active={loading} className="me-3" />
                <div>
                  <Skeleton.Button
                    active={loading}
                    size="large"
                    shape="default"
                    block={true}
                  />
                  <Skeleton.Input
                    active={loading}
                    size="small"
                    className="mt-2"
                  />
                </div>
              </div>
            </Col>
            <Col lg="6">
              <Skeleton.Image
                active={loading}
                style={{ width: "100%", height: "200px" }}
              />
            </Col>
          </Row>
          <Skeleton active={loading} paragraph={{ rows: 15 }} />
        </div>
      ) : (
        <div>
          {/* Header Section */}
          <div className="school-header">
            <Row>
              <Col lg="6">
                {data.schoolLogo && data.name && (
                  <div className="school-logo-section">
                    <img
                      src={data.schoolLogo}
                      className="school-logo"
                      alt="School Logo"
                    />
                    <h1 className="school-name">{data.name}</h1>
                  </div>
                )}

                {/* Contact Information */}
                <div className="contact-info">
                  {data.address && (
                    <div className="contact-item">
                      <i className="fa fa-map-marker contact-icon"></i>
                      <span>{data.address}</span>
                    </div>
                  )}

                  {data.object?.phoneNumber && (
                    <div className="contact-item">
                      <i className="fa fa-phone contact-icon"></i>
                      <a
                        href={`tel:${data.object.phoneNumber}`}
                        className="contact-link"
                      >
                        {data.object.phoneNumber}
                      </a>
                    </div>
                  )}

                  {data.email && (
                    <div className="contact-item">
                      <i className="fa fa-envelope contact-icon"></i>
                      <a href={`mailto:${data.email}`} className="contact-link">
                        {data.email}
                      </a>
                    </div>
                  )}

                  {data.object?.schoolWebsite && (
                    <div className="contact-item">
                      <i className="fa fa-globe contact-icon"></i>
                      <a
                        href={data.object.schoolWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-link"
                      >
                        {data.object.schoolWebsite}
                      </a>
                    </div>
                  )}
                </div>
                <div>
                  {/* Student Login Link */}
                  <Link
                    to={`/student-login/${data.schoolKey}`}
                    className="student-login-link"
                  >
                    <i className="fa fa-user"></i>
                    Student Login
                  </Link>
                </div>
              </Col>

              <Col lg="6">
                <div className="school-image-container">
                  {data.object?.schoolPoster ? (
                    <img
                      src={data.object.schoolPoster.url}
                      alt="School Poster"
                      className="school-image"
                    />
                  ) : (
                    <img
                      src={SchoolImage}
                      alt="School Image"
                      className="school-image"
                    />
                  )}
                </div>
              </Col>
            </Row>
          </div>

          {/* About School Section */}
          {data.aboutSchool && (
            <div className="about-school">
              <h3 className="about-school-title">
                <i className="fas fa-school"></i>
                About School
              </h3>
              <p className="about-school-text">{data.aboutSchool}</p>

              {/* Terms of Use */}
              <div className='pt-3'>
                {data.terms && (
                  <Link
                    to={`/terms-of-use/${data.schoolKey}`}
                    className="terms-link"
                  >
                    <i className="fa fa-info-circle"></i>
                    Terms of Use
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Social Media Links */}
          {(data.facebook ||
            data.instagram ||
            data.twitter ||
            data.object?.youtube) && (
            <div className="social-media-section">
              <h3 className="social-media-title">
                <i className="fa fa-share-alt"></i>
                Connect With Us
              </h3>
              <div className="social-links">
                {data.facebook && (
                  <a
                    href={data.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <i className="fa-brands fa-facebook social-icon"></i>
                    Facebook
                  </a>
                )}
                {data.instagram && (
                  <a
                    href={data.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <i className="fa-brands fa-instagram social-icon"></i>
                    Instagram
                  </a>
                )}
                {data.twitter && (
                  <a
                    href={data.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <i className="fa-brands fa-twitter social-icon"></i>
                    Twitter
                  </a>
                )}
                {data.object?.youtube && (
                  <a
                    href={data.object.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <i className="fa-brands fa-youtube social-icon"></i>
                    YouTube
                  </a>
                )}
              </div>
            </div>
          )}

          <hr
            style={{
              margin: "3rem 0",
              border: "none",
              borderTop: "2px solid #e9ecef",
            }}
          />

          {/* Services, Facilities, Staff Cards */}
          <Row className="info-cards-section">
            {data.services && data.services.length > 0 && (
              <Col md="4" className="mb-4">
                <div className="info-card">
                  <h4 className="info-card-title">Services Offered</h4>
                  <ul className="info-list">
                    {data.services.map((service, index) => (
                      <li key={index} className="info-list-item">
                        <i className="fa fa-check check-icon"></i>
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            )}

            {data.facilities && data.facilities.length > 0 && (
              <Col md="4" className="mb-4">
                <div className="info-card">
                  <h4 className="info-card-title">Facilities Available</h4>
                  <ul className="info-list">
                    {data.facilities.map((facility, index) => (
                      <li key={index} className="info-list-item">
                        <i className="fa fa-check check-icon"></i>
                        {facility}
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            )}

            {data.staff && data.staff.length > 0 && (
              <Col md="4" className="mb-4">
                <div className="info-card">
                  <h4 className="info-card-title">Our Staff</h4>
                  <ul className="info-list">
                    {data.staff.map((staffMember, index) => (
                      <li key={index} className="info-list-item">
                        <i className="fa fa-check check-icon"></i>
                        {staffMember}
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            )}
          </Row>

          {/* Enquiry Button */}
          {data.id && (
            <Button onClick={handleEnquiryFormClick} className="enquiry-button">
              <i className="fa fa-envelope me-2"></i>
              Enquire Now
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default SchoolDetails;